import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  ApiResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterLink } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { CustomerList } from '../../../models/customer.model';
import { CustomerService } from '../../../services/customer.service';
import { DatePipe } from '@angular/common';
import { PhoneFormatPipe } from '../../../../shared/pipes/format-phone.pipe';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-customer-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ProgressSpinnerModule,
    RouterLink,
    ToastModule,
    DatePipe,
  ],
  templateUrl: './customer-list.component.html',
})
export class CustomerListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private customerService = inject(CustomerService);
  private messageService = inject(MessageService);
  private phoneFormatPipe = new PhoneFormatPipe();
  paginationService = inject(PaginationService);

  customers = signal<CustomerList[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  fullNameFilter?: string;
  emailFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.customerService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.fullNameFilter,
        this.emailFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          let customers = page.items as CustomerList[];
          customers = customers.map((c) => ({
            ...c,
            phone: this.formatPhoneNumber(c.phone || ''),
          }));
          this.customers.set(customers);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os clientes',
              life: 3000,
            });
          }
        },
      });
  }

  private formatPhoneNumber(phone: string): string {
    const coutryCode = phone.split(/\s+/)[0] as string;
    return this.phoneFormatPipe.transform(phone, coutryCode, {
      includeCountryCode: true,
    });
  }
}
